import { I18nTranslationService } from './../../core/services/i18n-translation.service';
import { SettingsService } from './../../settings/settings.service';
import { Router, RouterStateSnapshot } from '@angular/router';
import { StoreService, SubscriptionLike } from './../../core/services/store.service';
import { environment } from './../../../environments/environment';
import { SessionStorageService } from './../../core/services/session-storage.service';
import { RouterHelper } from 'src/app/core/router-helper';
import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { QuickTradeDialogService } from 'src/app/markets/order-types/quick-trade-dialog.service';

type GetLoginUrl = (router: Router, state: RouterStateSnapshot) => void;

@Component({
  standalone: false,
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeaderComponent implements OnInit, OnDestroy {

  announcements: any[];
  getLoginUrl: GetLoginUrl = RouterHelper.getLoginUrl;
  isIn: boolean = false;
  loggedIn: boolean;
  subs: SubscriptionLike[] = [];
  unreadAnnouncements: boolean = false;
  assetsPath: string = '/assets/chainex/images/pages/home/';
  username: string;
  darkTheme: boolean = true;

  headerStats: boolean = environment.config.HEADER_STATS;

  exchangeName: string = environment.config.EXCHANGE_NAME;

  siteName: String = environment.config.EXCHANGE_NAME_L;

  verificationLevel: number = 0;
  pushConnected: boolean = false;
  private interval: NodeJS.Timeout;

  constructor(
    public router: Router,
    private sessionStorage: SessionStorageService,
    private translateService: I18nTranslationService,
    public snackbar: MatSnackBar,
    private store: StoreService,
    private service: SettingsService,
    private quickTradeDialogs: QuickTradeDialogService,
    private cdr: ChangeDetectorRef
  ) {
    try {
      this.darkTheme = this.sessionStorage.get('THEME') !== 'light-theme';
    } catch {
      this.darkTheme = true;
    }
  }

  ngOnInit() {
    this.interval = setInterval((() => {
      this.pushConnected = this.store.pushHost._websocket_connected;
      this.cdr.detectChanges();
    }).bind(this), 2500);
    this.subs.push(this.sessionStorage.observe('LOGGED_IN').subscribe(
      (data) => {
        if (data !== '' &&
          this.sessionStorage.get('TOKEN') !== '') {

          this.filterMenu();
        }
        this.loggedIn = data === 'logged_in';

        if (this.loggedIn && typeof this.store.getAnnouncements === 'function') {
          this.store.getAnnouncements();
        }
        this.cdr.detectChanges();
      }
    ));

    this.subs.push(this.store.subscribe('announcements').subscribe((response: any) => {
      if (!response.refresh) {
        const temp: any[] = [];
        for (let i = 0; i < response.data.length; i++) {
          if (!!response.data[i].active && response.data[i].active === '1') {
            temp.push(response.data[i]);
          }
        }
        this.announcements = temp;
        this.getUnreadAnnouncements();
        this.cdr.detectChanges();
      }
    }));

    this.subs.push(this.sessionStorage.observe('THEME')
      .subscribe(data => {
        if (data === 'dark-theme' || data === 'light-theme') {
          this.darkTheme = data === 'dark-theme';
          this.cdr.detectChanges();
        }
      }));

    this.subs.push(this.sessionStorage.observe('PROFILE_VERIFICATION_LEVEL')
      .subscribe(data => {
        this.verificationLevel = Number(data ?? 0);
        this.cdr.detectChanges();
      }));
  }

  ngOnDestroy() {
    this.subs.forEach(sub => sub.unsubscribe());
    if (this.interval) {
      clearInterval(this.interval);
    }
  }

  openQuickBuySellModal(orderType: number, event: Event): void {
    event.preventDefault();
    if (!this.loggedIn || this.verificationLevel < 2) {
      return;
    }
    this.openDialog(orderType);
    this.hideNav();
  }

  public announcementsRead() {
    this.store.setAnnouncementsRead();
    if (this.loggedIn) {
      this.store.commitAnnouncementsRead();
      this.getUnreadAnnouncements();
    }
    this.cdr.detectChanges();
  }

  filterMenu() {
    if (this.sessionStorage.get('PROFILE_FIRST_NAME') === '') {
      this.username = this.sessionStorage.get('PROFILE_EMAIL');
    } else {
      this.username = this.sessionStorage.get('PROFILE_FIRST_NAME');
    }
  }

  getSupportUrl(): string {
    return environment.config.SUPPORT_URL;
  }

  public getUnreadAnnouncements() {
    this.unreadAnnouncements = false;
    this.announcements.forEach((item: any) => {
      if (item.read !== '1') { this.unreadAnnouncements = true; }
    });
    this.cdr.detectChanges();
  }

  hideNav() {
    this.isIn = false;
    this.cdr.detectChanges();
  }

  changeTheme(theme: string) {
    // Optimistic update: apply instantly, roll back only if the API rejects.
    this.sessionStorage.store('THEME', theme);
    this.darkTheme = theme === 'dark-theme';
    if (this.loggedIn) {
      const data: any = {
        dark_theme: theme === 'dark-theme' ? 1 : 0
      };
      this.service.updateSettings(data)
        .subscribe((response: any) => {
          if (response.response !== 'success') {
            const fallback = theme === 'dark-theme' ? 'light-theme' : 'dark-theme';
            this.sessionStorage.store('THEME', fallback);
            this.darkTheme = fallback === 'dark-theme';
          }
          if (response.response === 'success' || response.response === 'failure') {
            this.snackbar.open(this.translateService.translateResponse(response.reason), $localize`Close`,
              { duration: 2000 });
          }
        });
    }
    this.cdr.detectChanges();
  }

  logout() {
    this.sessionStorage.store('LOGGED_IN', '');
    this.hideNav();
    this.cdr.detectChanges();
  }

  navlink(): string {
    return (this.loggedIn ? '/dashboard' : '/');
  }

  toggleState() {
    this.isIn = !this.isIn;
    this.cdr.detectChanges();
  }

  openDialog(type: number = 0): void {
    if (this.loggedIn && this.verificationLevel < 2) {
      return;
    }

    void this.quickTradeDialogs.openWizard({
      width: '500px',
      maxHeight: '90vh',
      position: { top: '52px', right: '25px'},
      data: {type}
    });
  }

}
